import React, { useState } from 'react';

/**
 * Public SMS Opt-In page — Twilio TFN (Toll-Free Number) compliance.
 *
 * Available at /sms-optin without login. Serves as proof-of-consent
 * location required by carriers for outbound SMS to US toll-free numbers.
 *
 * Records to backend collection `sms_optins`:
 *   phone, consent, opt_in_at (server), ip_address (server), user_agent.
 */
export default function SmsOptInPage() {
  const [phone, setPhone] = useState('');
  const [consent, setConsent] = useState(false);
  const [status, setStatus] = useState({ type: null, message: '' });
  const [submitting, setSubmitting] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!consent) {
      setStatus({ type: 'error', message: 'Please check the consent box to continue.' });
      return;
    }
    setSubmitting(true);
    setStatus({ type: null, message: '' });
    try {
      const res = await fetch(`${BACKEND}/api/sms-optin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone: phone.trim(), consent: true }),
      });
      const data = await res.json();
      if (!res.ok) {
        const detail = typeof data.detail === 'string'
          ? data.detail
          : 'Something went wrong. Please check your phone number and try again.';
        setStatus({ type: 'error', message: detail });
      } else {
        setStatus({ type: 'success', message: data.message || 'Opt-in recorded. Thank you.' });
        setPhone('');
        setConsent(false);
      }
    } catch (err) {
      setStatus({ type: 'error', message: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={styles.page} data-testid="sms-optin-page">
      <div style={styles.card}>
        <h1 style={styles.h1} data-testid="sms-optin-title">SMS Notification Opt-In</h1>
        <p style={styles.lede}>
          Opt in below to receive text-message updates from DealShaq about your
          account, orders, delivery status, and customer support.
        </p>

        <form onSubmit={handleSubmit} noValidate data-testid="sms-optin-form">
          <label style={styles.label} htmlFor="sms-optin-phone">Phone number</label>
          <input
            id="sms-optin-phone"
            type="tel"
            name="phone"
            required
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1 555 123 4567"
            style={styles.input}
            data-testid="sms-optin-phone-input"
          />

          <label style={styles.consentRow}>
            <input
              type="checkbox"
              name="sms_consent"
              required
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              style={styles.checkbox}
              data-testid="sms-optin-consent-checkbox"
            />
            <span>I agree to receive SMS notifications from DealShaq.</span>
          </label>

          <div style={styles.disclosure} data-testid="sms-optin-disclosure">
            <p style={styles.disclosureP}>
              By entering your phone number and clicking &ldquo;Submit,&rdquo; you agree to
              receive SMS messages from DealShaq related to your account, orders, delivery
              updates, and customer support. Message frequency varies. Message and data
              rates may apply.
            </p>
            <p style={styles.disclosureP}>
              You can opt out at any time by replying STOP. For help, reply HELP or contact
              support@dealshaq.com.
            </p>
            <p style={{ ...styles.disclosureP, marginBottom: 0 }}>
              Your consent is not a condition of purchase. We store your opt-in timestamp,
              IP address, and phone number securely for compliance purposes.
            </p>
          </div>

          <button
            type="submit"
            disabled={submitting}
            style={{ ...styles.button, opacity: submitting ? 0.6 : 1 }}
            data-testid="sms-optin-submit-button"
          >
            {submitting ? 'Submitting…' : 'Submit'}
          </button>

          {status.type === 'success' && (
            <div style={styles.success} data-testid="sms-optin-success">
              {status.message}
            </div>
          )}
          {status.type === 'error' && (
            <div style={styles.error} data-testid="sms-optin-error">
              {status.message}
            </div>
          )}
        </form>

        <p style={styles.footer}>
          Read our{' '}
          <a href="/terms" style={styles.link}>Terms of Service</a> and{' '}
          <a href="/privacy" style={styles.link}>Privacy Policy</a>.
        </p>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: '100vh',
    background: '#f7f7f5',
    padding: '48px 16px',
    display: 'flex',
    justifyContent: 'center',
    fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#1a1a1a',
  },
  card: {
    maxWidth: 620,
    width: '100%',
    background: '#fff',
    borderRadius: 12,
    padding: '40px 32px',
    boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
  },
  h1: { margin: '0 0 12px', fontSize: 28, fontWeight: 700 },
  lede: { margin: '0 0 28px', color: '#4a4a4a', lineHeight: 1.5 },
  label: { display: 'block', fontWeight: 600, marginBottom: 6 },
  input: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 16,
    borderRadius: 8,
    border: '1px solid #d0d0cc',
    marginBottom: 20,
    boxSizing: 'border-box',
  },
  consentRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 20,
    lineHeight: 1.5,
    cursor: 'pointer',
  },
  checkbox: { marginTop: 4, width: 18, height: 18, flexShrink: 0 },
  disclosure: {
    background: '#f0efeb',
    borderLeft: '3px solid #2d5f3f',
    padding: '16px 18px',
    fontSize: 14,
    lineHeight: 1.55,
    color: '#333',
    borderRadius: 4,
    marginBottom: 24,
  },
  disclosureP: {
    margin: '0 0 10px',
  },
  button: {
    background: '#2d5f3f',
    color: '#fff',
    border: 'none',
    padding: '13px 28px',
    fontSize: 16,
    fontWeight: 600,
    borderRadius: 8,
    cursor: 'pointer',
    minWidth: 140,
  },
  success: {
    marginTop: 20,
    padding: '12px 16px',
    background: '#e8f4ed',
    border: '1px solid #2d5f3f',
    borderRadius: 8,
    color: '#1e4a2d',
  },
  error: {
    marginTop: 20,
    padding: '12px 16px',
    background: '#fdecea',
    border: '1px solid #c63030',
    borderRadius: 8,
    color: '#7a1616',
  },
  footer: { marginTop: 24, fontSize: 13, color: '#777' },
  link: { color: '#2d5f3f', textDecoration: 'underline' },
};
